import {useCallback, useMemo} from 'react';
import {
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
  QueryStatus,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {buildCancelTokenSource, CancelError, CancelToken} from '../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {getCompany} from '../../store/data/company/companySelectors';

/*
 * Types.
 */

export interface SettingsInvalidateQueriesOptions {
  shouldUncache?: boolean; // default = false
}

type SettingsQueryFunction<T = unknown, TQueryKey extends QueryKey = QueryKey> = (
  context: QueryFunctionContext<TQueryKey> & {company: CompanyModel; cancelToken: CancelToken},
) => T | Promise<T>;

interface UseSettingsQueryReturnType {
  asyncStatus: AsyncStatusesEnum;
}

interface UseSettingsInfiniteQueryReturnType {
  asyncStatus: AsyncMoreStatusesEnum;
}

export interface UseSettingsQueryOptions
  extends Pick<
    UseQueryOptions,
    | 'suspense'
    | 'enabled'
    | 'keepPreviousData'
    | 'refetchOnMount'
    | 'refetchOnReconnect'
    | 'refetchOnWindowFocus'
  > {}

/*
 * Hooks.
 */

/**
 * Wrap useQuery from React Query with Front defaults.
 *
 * Injects 'company' and 'cancelToken' in 'queryFn'.
 * Returns 'asyncStatus' wrapping status with 'AsyncStatusesEnum'.
 *
 * NB: Types from React Query.
 */
export function useSettingsQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: SettingsQueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData, TError> & UseSettingsQueryReturnType {
  const company = useAppSelector(getCompany);

  const wrappedQuery: QueryFunction<TQueryFnData, TQueryKey> = useCallback(
    (context) => {
      const cancelToken = getCancelTokenFromSignal(context.signal);
      return queryFn({...context, company, cancelToken});
    },
    [company, queryFn],
  );

  const wrappedOptions = useMemo(() => {
    if (!options) {
      return undefined;
    }

    return {
      ...options,
      onError: wrapOnError(options.onError),
    };
  }, [options]);

  const query = useQuery(queryKey, wrappedQuery, wrappedOptions);

  return useMemo(
    () => ({
      ...query,
      asyncStatus: getAsyncStatus(query.status),
    }),
    [query],
  );
}

/**
 * Wrap useInfiniteQuery from React Query with Front defaults.
 *
 * Injects 'company' and 'cancelToken' in 'queryFn'.
 * Returns 'asyncStatus' wrapping status with 'AsyncMoreStatusesEnum'.
 *
 * NB: Types from React Query.
 */
export function useSettingsInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: SettingsQueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>,
    'queryKey' | 'queryFn'
  >,
): UseInfiniteQueryResult<TData, TError> & UseSettingsInfiniteQueryReturnType {
  const company = useAppSelector(getCompany);

  const wrappedQuery: QueryFunction<TQueryFnData, TQueryKey> = useCallback(
    (context) => {
      const cancelToken = getCancelTokenFromSignal(context.signal);
      return queryFn({...context, cancelToken, company});
    },
    [company, queryFn],
  );

  const wrappedOptions = useMemo(() => {
    if (!options) {
      return undefined;
    }

    return {
      ...options,
      onError: wrapOnError(options.onError),
    };
  }, [options]);

  const query = useInfiniteQuery(queryKey, wrappedQuery, wrappedOptions);

  return useMemo(
    () => ({
      ...query,
      asyncStatus: getLoadMoreStatus(query.status, query.hasNextPage),
    }),
    [query],
  );
}

export function useSettingsInvalidateQueries() {
  const queryClient = useQueryClient();

  return useCallback(
    (queryKey: QueryKey, options: SettingsInvalidateQueriesOptions = {}) => {
      const {shouldUncache = false} = options;

      if (shouldUncache) {
        queryClient.removeQueries(queryKey);
      } else {
        queryClient.invalidateQueries(queryKey);
      }
    },
    [queryClient],
  );
}

/*
 * Helpers.
 */

// https://react-query.tanstack.com/guides/query-cancellation#using-an-axios-version-less-than-v0220
function getCancelTokenFromSignal(signal: AbortSignal | undefined): CancelToken {
  const cancelToken = buildCancelTokenSource();

  signal?.addEventListener('abort', () => {
    cancelToken.cancel();
  });

  return cancelToken.token;
}

function getAsyncStatus(status: QueryStatus): AsyncStatusesEnum {
  switch (status) {
    case 'success':
      return AsyncStatusesEnum.LOADED;
    case 'error':
      return AsyncStatusesEnum.FAILED;
    case 'idle':
    case 'loading':
      return AsyncStatusesEnum.LOADING;
    default:
      throw new UnreachableError(status);
  }
}

function getLoadMoreStatus(status: QueryStatus, hasNextPage: boolean | undefined) {
  switch (status) {
    case 'loading':
    case 'idle':
      return AsyncMoreStatusesEnum.LOADING;
    case 'error':
      return AsyncMoreStatusesEnum.ERROR;
    case 'success':
      return hasNextPage ? AsyncMoreStatusesEnum.HAS_MORE : AsyncMoreStatusesEnum.ALL_LOADED;
    default:
      throw new UnreachableError(status);
  }
}

function wrapOnError(onError?: HandlerOf<any>) {
  if (!onError) {
    return undefined;
  }

  return (error: any) => {
    if (error instanceof CancelError) {
      return;
    }

    onError(error);
  };
}
